import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import { FC, useEffect, useState } from 'react';
import PinInput from 'src/components/signature/pin-input';
import SetupSigningWizard from 'src/components/signature/setup-signing-wizard';
import SignatureRender from 'src/components/signature/signature-render';
import useCurrentUser from 'src/hooks/use-current-user';
import { useSignatureValidation } from 'src/hooks/use-signature-validation';

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  //  consentConfirmation is the signer's answer to the legally-binding checkbox, recorded per
  //  signature — pass it through instead of assuming it at the service layer.
  onSign: (signatureName: string, consentConfirmation: boolean, pin?: string) => Promise<void>;
  signatureName: string;
  setSignatureName: (name: string) => void;
  //  THIN-7681 PIN-challenge error surfaced by the parent (wrong PIN / no PIN set).
  pinError?: string | null;
}

const SignatureModal: FC<SignatureModalProps> = ({
  open,
  onClose,
  onSign,
  signatureName,
  setSignatureName,
  pinError,
}) => {
  const { enabled, hasPin, statusLoading } = useSignatureValidation();
  const [
    firstName,
    lastName,
    licenseNumber,
    credentials,
    //  APIT-655: the therapist's display overrides for the signature block. Null
    //  for everyone today, which renders exactly as it did before.
    signatureLineOverride,
    credentialsLineOverride,
  ] = useCurrentUser([
    'firstName',
    'lastName',
    'licenseNumber',
    'credentials',
    'signatureLineOverride',
    'credentialsLineOverride',
  ]) as Array<string | null>;
  const fullName = [lastName, firstName].filter(Boolean).join(' ');

  const [pin, setPin] = useState('');
  const [agree, setAgree] = useState(false);
  const showSetup = enabled && !hasPin && !statusLoading;

  useEffect(() => {
    if (open) setPin('');
  }, [open]);
  useEffect(() => {
    if (pinError) setPin('');
  }, [pinError]);

  const handleSign = async () => {
    if (enabled) {
      if (pin.length !== 4 || !agree) return;
      await onSign(fullName, agree, pin); //  view-only name; backend HR-renders the signed name
      return;
    }
    if (!signatureName.trim() || !agree) return;
    await onSign(signatureName, agree);
    setSignatureName('');
  };

  const handleClose = () => {
    setSignatureName('');
    setPin('');
    onClose();
  };

  const signDisabled = enabled ? pin.length !== 4 || !agree : !signatureName.trim() || !agree;

  const consent = (
    <Stack display={'flex'} flexDirection={'row'} alignItems={'flex-start'} mt={1}>
      <Checkbox checked={agree} onChange={(e: any) => setAgree(e?.target?.checked)} />
      <Typography variant="body2" flexWrap={'wrap'}>
        I agree that my electronic signature is legally binding and holds the same validity as my
        handwritten signature, in accordance with applicable laws and regulations.
      </Typography>
    </Stack>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {!showSetup && <DialogTitle>Sign Documents</DialogTitle>}
      <DialogContent>
        {showSetup ? (
          <SetupSigningWizard onCancel={handleClose} />
        ) : enabled ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Your signature
            </Typography>
            <SignatureRender
              name={fullName}
              license={licenseNumber}
              credentials={credentials}
              signatureLineOverride={signatureLineOverride}
              credentialsLineOverride={credentialsLineOverride}
            />
            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              Enter your 4-digit signing PIN
            </Typography>
            <PinInput value={pin} onChange={setPin} error={!!pinError} mask autoFocus />
            {pinError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {pinError}
              </Typography>
            )}
            {consent}
          </>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please enter your name to sign the approved documents
            </Typography>
            <TextField
              fullWidth
              label="Your Name"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              sx={{ mb: 2 }}
            />
            {signatureName && (
              <Typography
                sx={{
                  fontFamily: "'Dancing Script', cursive",
                  fontSize: '2rem',
                  color: 'primary.main',
                  textAlign: 'center',
                  mt: 2,
                }}
              >
                {signatureName}
              </Typography>
            )}
            {consent}
          </>
        )}
      </DialogContent>
      {!showSetup && (
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSign} disabled={signDisabled}>
            Sign Documents
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SignatureModal;
